<script lang="ts">
	import { page } from '$app/stores';
	import { currentLanguage, getLanguageFromPath, t } from '$lib/stores/i18n';
	import { authStore, logout } from '$lib/stores/user';
	import { onMount } from 'svelte';
	import { trackSelectContent } from '$lib/analytics/gtag';
	import LoginCard from './card/LoginCard.svelte';
	import NotificationCard from './card/NotificationCard.svelte';
	import ProfileCard from './card/ProfileCard.svelte';
	import LanguageSwitcher from './LanguageSwitcher.svelte';
	export let pageName = 'Home';
	export let onLogOut = () => {};

	// Get current language from path
	$: lang = getLanguageFromPath($page.url.pathname);
	$: currentLanguage.set(lang);

	let isOpenLogin = false;
	let isOpenProfile = false;
	let isOpenNotification = false;
	let notificationCount = 3; // Mock notification count
	let isMobileMenuOpen = false;

	// Subscribe to auth store
	$: isAuthenticated = $authStore.isAuthenticated;
	$: currentUser = $authStore.user;

	async function handleLogout() {
		console.log('Logging out...');
		try {
			await logout();
			console.log('Logout successful');
		} catch (error) {
			console.error('Logout error:', error);
		}
		onLogOut();
	}

	function toggleNotificationDropdown() {
		isOpenNotification = !isOpenNotification;
		isOpenProfile = false; // Close profile when opening notification
	}

	function closeNotificationDropdown() {
		isOpenNotification = false;
	}

	function toggleProfileDropdown() {
		isOpenProfile = !isOpenProfile;
		isOpenNotification = false; // Close notification when opening profile
	}

	function closeProfileDropdown() {
		isOpenProfile = false;
	}

	function handleShowLogin() {
		isOpenLogin = true;
	}

	function handleLoginSuccess(user: any) {
		console.log('Login successful:', user);
		isOpenLogin = false;
	}

	function toggleMobileMenu() {
		isMobileMenuOpen = !isMobileMenuOpen;
	}

	function closeMobileMenu() {
		isMobileMenuOpen = false;
	}

	function trackNavClick(contentId: string, linkText: string) {
		trackSelectContent({
			content_type: 'nav_link',
			content_id: contentId,
			content_group: 'header',
			link_text: linkText
		});
	}

	// Close dropdown when clicking outside
	function handleClickOutside(event: MouseEvent) {
		// This is now handled by individual components
	}

	onMount(() => {
		document.addEventListener('click', handleClickOutside);
		return () => {
			document.removeEventListener('click', handleClickOutside);
		};
	});
</script>

<!-- header-area -->
<header id="header" class="transparent-header">
	<div id="header-fixed-height"></div>
	<div id="sticky-header" class="tg-header__area">
		<div class="container">
			<div class="row">
				<div class="col-12">
					<div class="tgmenu__wrap">
						<nav class="tgmenu__nav">
							<div class="logo">
								<a href="/{lang}"><img src="/assets/img/logo/logo.png" alt="Logo" /></a>
							</div>
							<div class="tgmenu__navbar-wrap tgmenu__main-menu d-none d-xl-flex">
								<ul class="navigation">
									<li class={pageName == 'Home' ? 'active' : ''}>
										<a href="/{lang}" rel="external" on:click={() => trackNavClick('home', $t('nav.home'))}>{$t('nav.home')}</a>
									</li>
									<li class={pageName == 'AI Solutions' ? 'active' : ''}>
										<a href="/{lang}/ai" rel="external" on:click={() => trackNavClick('ai', $t('nav.aiGenerated'))}
											>{$t('nav.aiGenerated')}</a
										>
									</li>
									<li class={pageName == 'Instant-Quote' ? 'active' : ''}>
										<a href="/{lang}/3dprintingservice" rel="external" on:click={() => trackNavClick('3dprintingservice', $t('nav.printingService'))}
											>{$t('nav.printingService')}</a
										>
									</li>
									<li class={pageName == 'Contact' ? 'active' : ''}>
										<a href="/{lang}#contact" rel="external" on:click={() => trackNavClick('contact', $t('nav.contact'))}>{$t('nav.contact')}</a>
									</li>
									<li style="display: flex; align-items: center; gap: 12px;">
										<LanguageSwitcher />
										{#if isAuthenticated}
											<!-- Notification Bell -->
											<NotificationCard
												isOpen={isOpenNotification}
												{notificationCount}
												onClose={closeNotificationDropdown}
												onToggle={toggleNotificationDropdown}
											/>

											<!-- Profile Dropdown -->
											<ProfileCard
												isOpen={isOpenProfile}
												{currentUser}
												onClose={closeProfileDropdown}
												onToggle={toggleProfileDropdown}
												onLogout={handleLogout}
											/>
										{:else}
											<button
												class="login-btn"
												aria-label="Login"
												on:click={() => (isOpenLogin = true)}
											>
												<i class="fas fa-user"></i>
											</button>
										{/if}
									</li>
								</ul>
							</div>
							<div class="tgmenu__action">
								<ul class="list-wrap">
									<!-- <li class="header-btn">
										<a href="https://rebrand.3dai.co.th/pricing"  aria-label="Instagram" class="tg-btn">
											<img
												src="/assets/img/icons/btn_right_arrow.svg"
												alt="icon"
												class="right_arrow"
											/>
											<span>
												<img src="/assets/img/icons/btn_icon.svg" alt="" />
												try it now
											</span>
										</a>
									</li> -->
								</ul>
							</div>
							<button
								type="button"
								class="mobile-nav-toggler"
								on:click={toggleMobileMenu}
								aria-label="Toggle mobile menu"
								tabindex="0"
							>
								<i class="tg-flaticon-menu-1"></i>
							</button>
						</nav>
					</div>
				</div>
			</div>
		</div>
	</div>

	<!-- Mobile Menu  -->
	<div class="tgmobile__menu" class:active={isMobileMenuOpen}>
		<nav class="tgmobile__menu-box">
			<button
				type="button"
				aria-label="Close mobile menu"
				class="close-btn"
				on:click={closeMobileMenu}
			>
				<i class="tg-flaticon-close-1"></i>
			</button>
			<div class="nav-logo">
				<a href="/{lang}" on:click={closeMobileMenu}>
					<img src="/assets/img/logo/logo.png" alt="Logo" />
				</a>
			</div>
			<div class="mobile-auth-section">
				<div class="mobile-language-switcher">
					<LanguageSwitcher />
				</div>
				{#if isAuthenticated}
					<div class="mobile-user-actions">
						<div class="mobile-profile">
							<ProfileCard
								isOpen={isOpenProfile}
								{currentUser}
								onClose={closeProfileDropdown}
								onToggle={toggleProfileDropdown}
								onLogout={handleLogout}
								profileClassName="mobile-profile-dropdown"
							/>
							<NotificationCard
								isOpen={isOpenNotification}
								{notificationCount}
								onClose={closeNotificationDropdown}
								onToggle={toggleNotificationDropdown}
								notificationClassName="mobile-notification-dropdown"
							/>
						</div>
					</div>
				{:else}
					<button
						class="mobile-login-btn"
						aria-label="Login"
						on:click={() => {
							isOpenLogin = true;
							closeMobileMenu();
						}}
					>
						<i class="fas fa-user"></i>
						<span>{$t('common.login')}</span>
					</button>
				{/if}
			</div>
			<!-- <div class="tgmobile__search">
				<form action="#">
					<input type="text" placeholder="Search here..." />
					<button aria-label="Search"><i class="fas fa-search"></i></button>
				</form>
			</div> -->
			<div class="tgmobile__menu-outer">
				<ul class="navigation">
					<li class={pageName == 'Home' ? 'active' : ''}>
						<a href="/{lang}" rel="external" on:click={() => { trackNavClick('home', $t('nav.home')); closeMobileMenu(); }}>{$t('nav.home')}</a>
					</li>
					<li class={pageName == 'AI Solutions' ? 'active' : ''}>
						<a href="/{lang}/ai" rel="external" on:click={() => { trackNavClick('ai', $t('nav.aiGenerated')); closeMobileMenu(); }}
							>{$t('nav.aiGenerated')}</a
						>
					</li>
					<li class={pageName == 'Instant-Quote' ? 'active' : ''}>
						<a href="/{lang}/3dprintingservice" rel="external" on:click={() => { trackNavClick('3dprintingservice', $t('nav.printingService')); closeMobileMenu(); }}
							>{$t('nav.printingService')}</a
						>
					</li>
					<li class={pageName == 'Contact' ? 'active' : ''}>
						<a href="/{lang}#contact" rel="external" on:click={() => { trackNavClick('contact', $t('nav.contact')); closeMobileMenu(); }}
							>{$t('nav.contact')}</a
						>
					</li>
				</ul>
			</div>
			<div class="social-links">
				<ul class="list-wrap">
					<li>
						<a href="https://google.com" aria-label="Facebook"><i class="fab fa-facebook-f"></i></a>
					</li>
					<li>
						<a href="https://google.com" aria-label="Twitter"><i class="fab fa-twitter"></i></a>
					</li>
					<li>
						<a href="https://google.com" aria-label="Instagram"><i class="fab fa-instagram"></i></a>
					</li>
					<li>
						<a href="https://google.com" aria-label="LinkedIn"><i class="fab fa-linkedin-in"></i></a
						>
					</li>
					<li>
						<a href="https://google.com" aria-label="YouTube"><i class="fab fa-youtube"></i></a>
					</li>
				</ul>
			</div>
		</nav>
	</div>
	<button
		type="button"
		aria-label="Close mobile menu"
		class="tgmobile__menu-backdrop"
		class:active={isMobileMenuOpen}
		on:click={closeMobileMenu}
	></button>
	<!-- End Mobile Menu -->

	<!-- header-search -->
	<div class="search__popup">
		<div class="container">
			<div class="row">
				<div class="col-12">
					<div class="search__wrapper">
						<div class="search__close">
							<button aria-label="Search" type="button" class="search-close-btn">
								<svg
									width="18"
									height="18"
									viewBox="0 0 18 18"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										d="M17 1L1 17"
										stroke="currentColor"
										stroke-width="1.5"
										stroke-linecap="round"
										stroke-linejoin="round"
									></path>
									<path
										d="M1 1L17 17"
										stroke="currentColor"
										stroke-width="1.5"
										stroke-linecap="round"
										stroke-linejoin="round"
									></path>
								</svg>
							</button>
						</div>
						<div class="search__form">
							<form action="#">
								<div class="search__input">
									<input class="search-input-field" type="text" placeholder="Type keywords here" />
									<span class="search-focus-border"></span>
									<button aria-label="Search">
										<svg
											width="20"
											height="20"
											viewBox="0 0 20 20"
											fill="none"
											xmlns="http://www.w3.org/2000/svg"
										>
											<path
												d="M9.55 18.1C14.272 18.1 18.1 14.272 18.1 9.55C18.1 4.82797 14.272 1 9.55 1C4.82797 1 1 4.82797 1 9.55C1 14.272 4.82797 18.1 9.55 18.1Z"
												stroke="currentColor"
												stroke-width="1.5"
												stroke-linecap="round"
												stroke-linejoin="round"
											></path>
											<path
												d="M19.0002 19.0002L17.2002 17.2002"
												stroke="currentColor"
												stroke-width="1.5"
												stroke-linecap="round"
												stroke-linejoin="round"
											></path>
										</svg>
									</button>
								</div>
							</form>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
	<div class="search-popup-overlay"></div>
	<!-- header-search-end -->

	<LoginCard
		isOpen={isOpenLogin}
		onClose={() => (isOpenLogin = false)}
		onLoginSuccess={handleLoginSuccess}
	/>
</header>

<!-- header-area-end -->

<style scoped>
	.mobile-profile {
		display: flex;
	}
	.login-btn {
		padding: 34px 18px;
		background: none;
		border: none;
		font-size: 20px;
		color: #fff;
		cursor: pointer;
		transition: color 0.3s ease;
	}

	.login-btn:hover {
		color: #4a6cf7;
	}

	/* Mobile Menu Styles */
	:global(.tgmobile__menu) {
		position: fixed !important;
		top: 0 !important;
		right: -100% !important;
		width: 300px !important;
		height: 100vh !important;
		background: #1a1a1a !important;
		z-index: 99999 !important;
		transition: right 0.3s ease !important;
		overflow-y: auto !important;
		transform: translateX(0) !important;
	}

	:global(.tgmobile__menu.active) {
		right: 0 !important;
		transform: translateX(0) !important;
	}

	:global(.tgmobile__menu-backdrop) {
		position: fixed !important;
		top: 0 !important;
		left: 0 !important;
		width: 100% !important;
		height: 100% !important;
		background: rgba(0, 0, 0, 0.5) !important;
		z-index: 99998 !important;
		opacity: 0 !important;
		visibility: hidden !important;
		transition: all 0.3s ease !important;
	}

	:global(.tgmobile__menu-backdrop.active) {
		opacity: 1 !important;
		visibility: visible !important;
	}

	:global(.tgmobile__menu-box) {
		padding: 20px !important;
		height: 100% !important;
		display: flex !important;
		flex-direction: column !important;
	}

	:global(.tgmobile__menu .close-btn) {
		position: absolute !important;
		top: 20px !important;
		right: 20px !important;
		background: none !important;
		border: none !important;
		color: #fff !important;
		font-size: 24px !important;
		cursor: pointer !important;
		z-index: 100000 !important;
	}

	:global(.tgmobile__menu .close-btn:hover) {
		color: #4a6cf7 !important;
	}

	:global(.tgmobile__menu .nav-logo) {
		margin-bottom: 30px !important;
		text-align: center !important;
	}

	:global(.tgmobile__menu .nav-logo img) {
		max-width: 150px !important;
		height: auto !important;
	}

	:global(.tgmobile__menu .tgmobile__search) {
		margin-bottom: 30px !important;
	}

	:global(.tgmobile__menu .tgmobile__search form) {
		display: flex !important;
		gap: 10px !important;
	}

	:global(.tgmobile__menu .tgmobile__search input) {
		flex: 1 !important;
		padding: 10px !important;
		border: 1px solid #333 !important;
		border-radius: 4px !important;
		background: #2a2a2a !important;
		color: #fff !important;
	}

	:global(.tgmobile__menu .tgmobile__search button) {
		padding: 10px 15px !important;
		background: #4a6cf7 !important;
		border: none !important;
		border-radius: 4px !important;
		color: #fff !important;
		cursor: pointer !important;
	}

	:global(.tgmobile__menu-outer) {
		flex: 1 !important;
		margin-bottom: 30px !important;
	}

	:global(.tgmobile__menu-outer .navigation) {
		list-style: none !important;
		padding: 0 !important;
		margin: 0 !important;
	}

	:global(.tgmobile__menu-outer .navigation li) {
		margin-bottom: 10px !important;
	}

	:global(.tgmobile__menu-outer .navigation a) {
		display: block !important;
		padding: 15px 20px !important;
		color: #fff !important;
		text-decoration: none !important;
		border-radius: 4px !important;
		transition: background 0.3s ease !important;
	}

	:global(.tgmobile__menu-outer .navigation a:hover),
	:global(.tgmobile__menu-outer .navigation li.active a) {
		background: #4a6cf7 !important;
		color: #fff !important;
	}

	:global(.tgmobile__menu .mobile-auth-section) {
		border-top: 1px solid #333 !important;
		padding-top: 20px !important;
		margin-top: 20px !important;
	}

	:global(.tgmobile__menu .mobile-language-switcher) {
		margin-bottom: 15px !important;
		display: flex !important;
		justify-content: center !important;
	}

	:global(.tgmobile__menu .mobile-user-actions) {
		display: flex !important;
		flex-direction: column !important;
		gap: 15px !important;
	}

	:global(.tgmobile__menu .mobile-login-btn) {
		display: flex !important;
		align-items: center !important;
		gap: 10px !important;
		padding: 15px 20px !important;
		background: #4a6cf7 !important;
		border: none !important;
		border-radius: 4px !important;
		color: #fff !important;
		cursor: pointer !important;
		font-size: 16px !important;
		width: 100% !important;
		transition: background 0.3s ease !important;
	}

	:global(.tgmobile__menu .mobile-login-btn:hover) {
		background: #3a5ae7 !important;
	}

	:global(.tgmobile__menu .social-links) {
		border-top: 1px solid #333 !important;
		padding-top: 20px !important;
	}

	:global(.tgmobile__menu .social-links .list-wrap) {
		display: flex !important;
		gap: 15px !important;
		justify-content: center !important;
		list-style: none !important;
		padding: 0 !important;
		margin: 0 !important;
	}

	:global(.tgmobile__menu .social-links a) {
		display: flex !important;
		align-items: center !important;
		justify-content: center !important;
		width: 40px !important;
		height: 40px !important;
		background: #333 !important;
		color: #fff !important;
		border-radius: 50% !important;
		text-decoration: none !important;
		transition: background 0.3s ease !important;
	}

	:global(.tgmobile__menu .social-links a:hover) {
		background: #4a6cf7 !important;
	}

	/* Mobile Navigation Toggler */
	:global(.mobile-nav-toggler) {
		display: none !important;
		cursor: pointer !important;
		padding: 10px !important;
		color: #fff !important;
		font-size: 24px !important;
	}

	:global(.mobile-nav-toggler:hover) {
		color: #4a6cf7 !important;
	}

	/* Responsive Design */
	@media (max-width: 1199px) {
		:global(.mobile-nav-toggler) {
			display: block !important;
		}

		:global(.tgmenu__navbar-wrap) {
			display: none !important;
		}

		:global(.tgmenu__action) {
			display: none !important;
		}
	}

	@media (max-width: 768px) {
		:global(.tgmobile__menu) {
			width: 280px !important;
		}

		:global(.tgmobile__menu .nav-logo img) {
			max-width: 120px !important;
		}
	}

	@media (max-width: 480px) {
		:global(.tgmobile__menu) {
			width: 100% !important;
			right: -100% !important;
		}

		:global(.tgmobile__menu.active) {
			right: 0 !important;
		}
	}
</style>
